import { Tab, Tabs } from '@mui/material';
import MuiButton from '@mui/material/Button';
import MuiIconButton from '@mui/material/IconButton';
import { alpha, keyframes, styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { getThemeSpacingCssPx, getThemeTypographyCssPx } from 'components/ui/utilities/theme';
import { getCssPx } from 'components/ui/utilities/zoom';
import { PRISM_BREAKPOINTS, RED } from 'theme';
import type {
  SecondaryNavigationActionButtonVariant,
  SecondaryNavigationIconButtonVariant,
  SecondaryNavigationInfoLabelTone,
  SecondaryNavigationResponsiveBehavior,
  SecondaryNavigationSelectorVariant,
  SecondaryNavigationStatusBadgeTone,
} from './SecondaryNavigation.types';

interface ZoomStyleProps {
  zoomLevel: number;
}

interface ResponsiveStyleProps extends ZoomStyleProps {
  responsiveBehavior?: SecondaryNavigationResponsiveBehavior;
}

const SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX = PRISM_BREAKPOINTS.tablet1024;
const SECONDARY_NAVIGATION_HORIZONTAL_PADDING_PX = 24;
const SECONDARY_NAVIGATION_TAB_LINE_HEIGHT_PX = 16;
const SECONDARY_NAVIGATION_TAB_PADDING_Y_PX = 8;
const getSecondaryNavigationTitleMaxWidth = () => `min(${getCssPx(600)}, 50vw)`;

const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';
const shouldForwardResponsiveProp = (prop: PropertyKey) => shouldForwardZoomProp(prop) && prop !== 'responsiveBehavior';
const shouldForwardActionButtonProp = (prop: PropertyKey) => prop !== 'buttonVariant' && prop !== 'buttonState';
const shouldForwardIconButtonProp = (prop: PropertyKey) => prop !== 'isActive' && prop !== 'iconButtonVariant';
const shouldForwardSelectorProp = (prop: PropertyKey) => prop !== 'selectorVariant';
const shouldForwardVisibleBadgeProp = (prop: PropertyKey) => prop !== 'isVisible';

interface ActionButtonStyleProps {
  buttonVariant: SecondaryNavigationActionButtonVariant;
  buttonState?: SecondaryNavigationActionButtonState;
}

interface ActionButtonContentItemStyleProps {
  contentPosition: SecondaryNavigationActionContentPosition;
}

interface IconButtonStyleProps {
  iconButtonVariant: SecondaryNavigationIconButtonVariant;
  isActive?: boolean;
}

interface NavigationValueToneStyleProps {
  valueTone: SecondaryNavigationInfoLabelTone;
}

const shouldForwardValueToneProp = (prop: PropertyKey) => prop !== 'valueTone';
const shouldForwardStatusBadgeProp = (prop: PropertyKey) => prop !== 'tone';
const shouldForwardStatusBadgeTextProp = (prop: PropertyKey) => prop !== 'textState';

interface StatusBadgeStyleProps {
  tone: SecondaryNavigationStatusBadgeTone;
}

interface StatusBadgeTextStyleProps {
  textState: 'entering' | 'exiting' | 'idle';
}

interface VisibleBadgeStyleProps {
  isVisible: boolean;
}

interface SelectorStyleProps {
  selectorVariant: SecondaryNavigationSelectorVariant;
}

type SecondaryNavigationActionButtonState = 'idle' | 'saving' | 'saved' | 'error';
type SecondaryNavigationActionContentPosition = 'active' | 'next' | 'previous';

const secondaryNavigationStatusBadgeTextEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(1px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const secondaryNavigationStatusBadgeTextExit = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-1px);
  }
`;

const getSecondaryNavigationStatusBadgeTextAnimation = (
  textState: StatusBadgeTextStyleProps['textState'],
  theme: Theme
) => {
  if (textState === 'entering') {
    return `${secondaryNavigationStatusBadgeTextEnter} 120ms ${theme.transitions.easing.easeOut}`;
  }

  if (textState === 'exiting') {
    return `${secondaryNavigationStatusBadgeTextExit} 80ms ${theme.transitions.easing.easeIn}`;
  }

  return 'none';
};

const secondaryNavigationSavePulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 14px 0 var(--secondary-navigation-save-glow-color);
  }
  50% {
    box-shadow: 0 0 24px 2px var(--secondary-navigation-save-glow-color);
  }
`;

const getSecondaryNavigationDisabledColors = (
  theme: Theme,
  colors: Theme['palette']['navigation']['secondary']['actionButton'][SecondaryNavigationActionButtonVariant],
  isActionState: boolean,
  isErrorState: boolean
) => {
  if (isErrorState) {
    return {
      backgroundColor: RED[600],
      borderColor: RED[600],
      color: theme.palette.common.white,
    };
  }

  if (isActionState) {
    return {
      backgroundColor: colors.background,
      borderColor: colors.border,
      color: colors.text,
    };
  }

  return {
    backgroundColor: colors.background,
    borderColor: colors.border,
    color: colors.text,
  };
};
const secondaryNavigationSavedCheckDraw = keyframes`
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

export const SecondaryNavigationRoot = styled('nav', {
  shouldForwardProp: shouldForwardResponsiveProp,
})<ResponsiveStyleProps>(({ responsiveBehavior = 'stack', theme }) => ({
  '--secondary-navigation-control-height': getCssPx(24),
  '--secondary-navigation-control-padding-x': getThemeSpacingCssPx(theme, 'xs'),
  '--secondary-navigation-control-padding-y': getThemeSpacingCssPx(theme, 'xs'),
  '--secondary-navigation-control-gap': getThemeSpacingCssPx(theme, 'xs'),
  '--secondary-navigation-control-radius': getCssPx(theme.shape.radius.xs),
  '--secondary-navigation-border-width': getCssPx(1),
  '--secondary-navigation-focus-outline-width': getCssPx(2),
  '--secondary-navigation-font-size': getThemeTypographyCssPx(theme, 'caption', 'fontSize'),
  '--secondary-navigation-filter-selector-max-width': getCssPx(172),
  '--secondary-navigation-line-height': getThemeTypographyCssPx(theme, 'caption', 'lineHeight'),
  '--secondary-navigation-action-icon-size': getCssPx(16),
  '--secondary-navigation-contained-icon-button-size': getCssPx(20),
  '--secondary-navigation-plain-icon-button-size': getCssPx(24),
  '--secondary-navigation-status-badge-font-size': getCssPx(10),
  '--secondary-navigation-status-badge-height': getCssPx(18),
  '--secondary-navigation-status-badge-line-height': getCssPx(14),
  '--secondary-navigation-ready-for-audit-badge-max-width': getCssPx(88),
  '--secondary-navigation-scalar-owner-icon-height': getCssPx(15),
  '--secondary-navigation-scalar-owner-icon-width': getCssPx(14),
  '--secondary-navigation-status-gap': getThemeSpacingCssPx(theme, 'xs'),
  '--secondary-navigation-status-badge-padding-x': getThemeSpacingCssPx(theme, 'xs'),
  '--secondary-navigation-status-badge-padding-y': getThemeSpacingCssPx(theme, 'xxs'),
  '--secondary-navigation-tab-height': getCssPx(32),
  '--secondary-navigation-padding-x': getCssPx(SECONDARY_NAVIGATION_HORIZONTAL_PADDING_PX),
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.bar.background,
  boxSizing: 'border-box',
  display: 'flex',
  flexShrink: 0,
  height: getCssPx(32),
  justifyContent: 'space-between',
  minWidth: 0,
  padding: '0 var(--secondary-navigation-padding-x)',
  width: '100%',
  [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    ...(responsiveBehavior === 'inline-scroll'
      ? { flexWrap: 'wrap' }
      : { alignItems: 'stretch', flexDirection: 'column' }),
    height: 'auto',
    minHeight: getCssPx(32),
    paddingBottom: getThemeSpacingCssPx(theme, 'xs'),
    paddingTop: getThemeSpacingCssPx(theme, 'xs'),
    rowGap: getThemeSpacingCssPx(theme, 'xs'),
  },
}));

export const SecondaryNavigationContent = styled('div', {
  shouldForwardProp: shouldForwardResponsiveProp,
})<ResponsiveStyleProps>(({ responsiveBehavior = 'stack', theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: responsiveBehavior === 'inline-scroll' ? '0 1 auto' : '1 1 auto',
  gap: getThemeSpacingCssPx(theme, 's'),
  minWidth: 0,
  overflowX: 'hidden',
  overflowY: 'hidden',
  [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]:
    responsiveBehavior === 'stack' ? { width: '100%' } : {},
}));

export const SecondaryNavigationTitleGroup = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(() => ({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 1 auto',
  minWidth: 0,
  '--secondary-navigation-title-leading-gap': getCssPx(4),
}));

export const SecondaryNavigationTitle = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => {
  const titleMaxWidth = getSecondaryNavigationTitleMaxWidth();

  return {
    color: theme.palette.navigation.secondary.title.text,
    display: 'block',
    flex: '0 1 auto',
    fontSize: getCssPx(16),
    fontWeight: theme.typography.fontWeightMedium,
    height: getCssPx(24),
    lineHeight: getCssPx(24),
    letterSpacing: 0,
    maxWidth: titleMaxWidth,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
      maxWidth: '100%',
    },
  };
});

export const SecondaryNavigationStatus = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(() => ({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  minHeight: getCssPx(18),
  '& > * + *': {
    marginLeft: 'var(--secondary-navigation-status-gap, 4px)',
  },
  '& > [data-secondary-navigation-ready-for-audit-badge="true"][aria-hidden="true"]': {
    marginLeft: 0,
  },
}));

export const SecondaryNavigationStatusBadgeRoot = styled('span', {
  shouldForwardProp: shouldForwardStatusBadgeProp,
})<StatusBadgeStyleProps>(({ theme, tone }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.statusBadge[tone].background,
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  boxSizing: 'border-box',
  color: theme.palette.navigation.secondary.statusBadge[tone].text,
  display: 'inline-flex',
  fontSize: 'var(--secondary-navigation-status-badge-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--secondary-navigation-status-badge-padding-x, 4px)',
  height: 'var(--secondary-navigation-status-badge-height, 18px)',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-status-badge-line-height, 14px)',
  padding:
    'var(--secondary-navigation-status-badge-padding-y, 2px) var(--secondary-navigation-status-badge-padding-x, 4px)',
  textAlign: 'center',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: 180,
    easing: theme.transitions.easing.easeOut,
  }),
  whiteSpace: 'nowrap',
  willChange: 'background-color, color',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const SecondaryNavigationStatusBadgeText = styled('span', {
  shouldForwardProp: shouldForwardStatusBadgeTextProp,
})<StatusBadgeTextStyleProps>(({ theme, textState }) => ({
  animation: getSecondaryNavigationStatusBadgeTextAnimation(textState, theme),
  display: 'inline-block',
  whiteSpace: 'nowrap',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const SecondaryNavigationReadyForAuditBadgeRoot = styled('span', {
  shouldForwardProp: shouldForwardVisibleBadgeProp,
})<VisibleBadgeStyleProps>(({ theme, isVisible }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderColor: alpha(theme.palette.primary.main, 0.28),
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  borderStyle: 'solid',
  borderWidth: isVisible ? theme.spacing(0.125) : 0,
  boxSizing: 'border-box',
  color: theme.palette.primary.main,
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'var(--secondary-navigation-status-badge-font-size, 10px)',
  fontWeight: theme.typography.fontWeightMedium,
  height: 'var(--secondary-navigation-status-badge-height, 18px)',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-status-badge-line-height, 14px)',
  maxWidth: isVisible ? 'var(--secondary-navigation-ready-for-audit-badge-max-width, 88px)' : 0,
  opacity: isVisible ? 1 : 0,
  overflow: 'hidden',
  padding: `var(--secondary-navigation-status-badge-padding-y, 2px) ${
    isVisible ? 'var(--secondary-navigation-status-badge-padding-x, 4px)' : '0'
  }`,
  pointerEvents: isVisible ? 'auto' : 'none',
  textAlign: 'center',
  transform: isVisible ? 'scale(1)' : 'scale(0.96)',
  transition: theme.transitions.create(['border-width', 'max-width', 'opacity', 'padding', 'transform'], {
    duration: 180,
    easing: theme.transitions.easing.easeOut,
  }),
  whiteSpace: 'nowrap',
  willChange: 'max-width, opacity, transform',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

// Warning-toned and wider than the audit badge, which caps at 88px.
export const SecondaryNavigationDailyNavOverrideBadgeRoot = styled(SecondaryNavigationReadyForAuditBadgeRoot)(
  ({ theme, isVisible }) => ({
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    borderColor: alpha(theme.palette.warning.main, 0.4),
    color: theme.palette.warning.dark,
    maxWidth: isVisible ? 'var(--secondary-navigation-daily-nav-override-badge-max-width, 140px)' : 0,
  })
);

export const SecondaryNavigationScalarOwnerBadgeRoot = styled('span', {
  shouldForwardProp: shouldForwardVisibleBadgeProp,
})<VisibleBadgeStyleProps>(({ theme, isVisible }) => ({
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'inline-flex',
  flexShrink: 0,
  height: 'var(--secondary-navigation-scalar-owner-icon-height, 15px)',
  justifyContent: 'center',
  marginRight: isVisible ? 'var(--secondary-navigation-title-leading-gap, 4px)' : 0,
  opacity: isVisible ? 1 : 0,
  overflow: 'hidden',
  pointerEvents: isVisible ? 'auto' : 'none',
  transform: isVisible ? 'scale(1)' : 'scale(0.8)',
  transition: theme.transitions.create(['margin-right', 'opacity', 'transform', 'width'], {
    duration: 180,
    easing: theme.transitions.easing.easeOut,
  }),
  width: isVisible ? 'var(--secondary-navigation-scalar-owner-icon-width, 14px)' : 0,
  willChange: 'opacity, transform, width',
  '& svg': {
    display: 'block',
    height: 'var(--secondary-navigation-scalar-owner-icon-height, 15px)',
    width: 'var(--secondary-navigation-scalar-owner-icon-width, 14px)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const SecondaryNavigationTitleAccessory = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  gap: getThemeSpacingCssPx(theme, 'xs'),
  minWidth: 0,
}));

export const SecondaryNavigationTabsFrame = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  minWidth: 0,
  position: 'relative',
});

export const SecondaryNavigationActiveIndicator = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.navigation.secondary.tab.selectedBackground,
  bottom: 0,
  display: 'block',
  height: 'var(--secondary-navigation-tab-height, 32px)',
  left: 0,
  opacity: 'var(--active-navigation-indicator-opacity, 0)',
  pointerEvents: 'none',
  position: 'absolute',
  transform: 'translateX(var(--active-navigation-indicator-x, 0px))',
  transition: theme.transitions.create(['opacity', 'transform', 'width'], {
    duration: 220,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--active-navigation-indicator-width, 0px)',
  zIndex: 0,
}));

export const SecondaryNavigationTabs = styled(Tabs)({
  flex: '1 1 auto',
  minHeight: 'unset',
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '& .MuiTabs-flexContainer': {
    alignItems: 'center',
    minWidth: 'max-content',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

export const SecondaryNavigationSkeletonTabs = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '0 1 auto',
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  minWidth: 0,
  overflow: 'hidden',
});

export const SecondaryNavigationTab = styled(Tab, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  alignItems: 'center',
  borderRadius: 0,
  boxSizing: 'border-box',
  color: theme.palette.navigation.secondary.tab.default,
  fontSize: getThemeTypographyCssPx(theme, 'caption', 'fontSize'),
  fontWeight: theme.typography.fontWeightMedium,
  height: getCssPx(32),
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: getCssPx(SECONDARY_NAVIGATION_TAB_LINE_HEIGHT_PX),
  minHeight: 'unset',
  minWidth: 'unset',
  opacity: 1,
  overflow: 'visible',
  padding: `${getCssPx(SECONDARY_NAVIGATION_TAB_PADDING_Y_PX)} ${getThemeSpacingCssPx(theme, 's')}`,
  textTransform: 'none',
  transition: theme.transitions.create(['background-color', 'color', 'outline-color'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  whiteSpace: 'nowrap',
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    color: theme.palette.navigation.secondary.tab.selectedText,
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.navigation.secondary.tab.selectedText,
  },
  '&.Mui-disabled': {
    color: theme.palette.navigation.secondary.tab.disabled,
    opacity: 1,
  },
  '&:hover:not(.Mui-disabled):not(.Mui-selected)': {
    backgroundColor: theme.palette.navigation.secondary.tab.hoverBackground,
    color: theme.palette.navigation.secondary.tab.selectedText,
  },
  '&:focus-visible': {
    outline: `var(--secondary-navigation-focus-outline-width, 2px) solid ${theme.palette.navigation.secondary.tab.focusOutline}`,
    outlineOffset: 'calc(var(--secondary-navigation-focus-outline-width, 2px) * -1)',
  },
}));

export const SecondaryNavigationTabLabel = styled('span')({
  display: 'block',
  lineHeight: 'inherit',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const SecondaryNavigationActions = styled('div', {
  shouldForwardProp: shouldForwardResponsiveProp,
})<ResponsiveStyleProps>(({ responsiveBehavior = 'stack', theme }) => ({
  alignItems: 'center',
  display: 'flex',
  // Keep actions elastic (both directions) so it stays live-responsive to available space —
  // a fixed size would freeze once collapsed and never re-expand as the window grows back.
  flexGrow: responsiveBehavior === 'inline-scroll' ? 1 : 0,
  flexShrink: responsiveBehavior === 'inline-scroll' ? 1 : 0,
  gap: getThemeSpacingCssPx(theme, 's'),
  justifyContent: 'flex-end',
  marginLeft: getThemeSpacingCssPx(theme, 's'),
  maxWidth: responsiveBehavior === 'inline-scroll' ? '100%' : undefined,
  minWidth: 0,
  ...(responsiveBehavior === 'inline-scroll' && {
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  }),
  [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    ...(responsiveBehavior === 'stack' && {
      justifyContent: 'flex-start',
      marginLeft: 0,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      width: '100%',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }),
  },
}));

export const SecondaryNavigationInfoLabelRoot = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.infoLabel.background,
  border: `var(--secondary-navigation-border-width, 1px) solid ${theme.palette.navigation.secondary.infoLabel.border}`,
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  boxSizing: 'border-box',
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'var(--secondary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  height: 'var(--secondary-navigation-control-height, 24px)',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-line-height, 16px)',
  padding: 'var(--secondary-navigation-control-gap, 4px)',
  textAlign: 'center',
  whiteSpace: 'nowrap',
}));

export const SecondaryNavigationInfoLabelText = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.navigation.secondary.infoLabel.label,
  display: 'inline-flex',
  minWidth: 0,
}));

export const SecondaryNavigationInfoLabelValue = styled('span', {
  shouldForwardProp: shouldForwardValueToneProp,
})<NavigationValueToneStyleProps>(({ theme, valueTone }) => ({
  alignItems: 'center',
  color: theme.palette.navigation.secondary.infoLabel.value[valueTone],
  display: 'inline-flex',
  minWidth: 0,
}));

export const SecondaryNavigationActionButtonRoot = styled(MuiButton, {
  shouldForwardProp: shouldForwardActionButtonProp,
})<ActionButtonStyleProps>(({ buttonState = 'idle', buttonVariant, theme }) => {
  const { actionButton } = theme.palette.navigation.secondary;
  const colors = actionButton[buttonVariant];
  const isActionState = buttonState !== 'idle';
  const isPulseVisible = buttonState === 'saving';
  const isErrorState = buttonState === 'error';
  const disabledColors = getSecondaryNavigationDisabledColors(theme, colors, isActionState, isErrorState);
  const variantStyles = {
    ghost: {
      backgroundColor: actionButton.ghost.background,
      borderColor: actionButton.ghost.border,
      color: actionButton.ghost.text,
      '&:hover': {
        backgroundColor: actionButton.ghost.hoverBackground,
        color: actionButton.ghost.hoverText,
      },
    },
    primary: {
      backgroundColor: actionButton.primary.background,
      borderColor: actionButton.primary.border,
      color: actionButton.primary.text,
      '&:hover': {
        backgroundColor: actionButton.primary.hoverBackground,
        borderColor: actionButton.primary.hoverBorder,
      },
    },
    positive: {
      backgroundColor: actionButton.positive.background,
      borderColor: actionButton.positive.border,
      color: actionButton.positive.text,
      '&:hover': {
        backgroundColor: actionButton.positive.hoverBackground,
        borderColor: actionButton.positive.hoverBorder,
      },
    },
    secondary: {
      backgroundColor: actionButton.secondary.background,
      borderColor: actionButton.secondary.border,
      color: actionButton.secondary.text,
      '&:hover': {
        backgroundColor: actionButton.secondary.hoverBackground,
        borderColor: actionButton.secondary.hoverBorder,
        color: actionButton.secondary.hoverText,
      },
    },
  } satisfies Record<SecondaryNavigationActionButtonVariant, object>;

  return {
    '--secondary-navigation-save-glow-color': alpha(isErrorState ? RED[400] : colors.background, 0.5),
    alignItems: 'center',
    animation: isPulseVisible ? `${secondaryNavigationSavePulse} 1200ms ease-in-out infinite` : undefined,
    borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
    borderStyle: 'solid',
    borderWidth: 'var(--secondary-navigation-border-width, 1px)',
    boxShadow: isPulseVisible ? `0 0 20px 0 ${alpha(isErrorState ? RED[400] : colors.background, 0.5)}` : 'none',
    display: 'inline-flex',
    fontSize: 'var(--secondary-navigation-font-size, 12px)',
    fontWeight: theme.typography.fontWeightMedium,
    gap: 'var(--secondary-navigation-control-gap, 4px)',
    height: 'var(--secondary-navigation-control-height, 24px)',
    justifyContent: 'center',
    letterSpacing: 0,
    lineHeight: 'var(--secondary-navigation-line-height, 16px)',
    minHeight: 'unset',
    minWidth: 'unset',
    overflow: 'hidden',
    padding: 'var(--secondary-navigation-control-padding-y, 4px) var(--secondary-navigation-control-padding-x, 4px)',
    textTransform: 'none',
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
      duration: 160,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&.Mui-disabled': {
      backgroundColor: disabledColors.backgroundColor,
      borderColor: disabledColors.borderColor,
      color: disabledColors.color,
      opacity: 0.65,
    },
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: 'inherit',
      margin: 0,
      '& svg': {
        fontSize: 'var(--secondary-navigation-action-icon-size, 16px)',
      },
    },
    ...variantStyles[buttonVariant],
    ...(isErrorState
      ? {
        backgroundColor: RED[600],
        borderColor: RED[600],
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: RED[700],
          borderColor: RED[700],
          color: theme.palette.common.white,
        },
      }
      : {}),
  };
});

export const SecondaryNavigationActionButtonContent = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  height: 'var(--secondary-navigation-line-height, 16px)',
  justifyContent: 'center',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
  transition: theme.transitions.create(['width'], {
    duration: 240,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

export const SecondaryNavigationActionButtonContentItem = styled('span', {
  shouldForwardProp: prop => prop !== 'contentPosition',
})<ActionButtonContentItemStyleProps>(({ contentPosition, theme }) => {
  const contentTranslateY = (() => {
    if (contentPosition === 'active') {
      return 0;
    }

    if (contentPosition === 'next') {
      return 'calc(var(--secondary-navigation-line-height, 16px) * -1)';
    }

    return 'var(--secondary-navigation-line-height, 16px)';
  })();

  return {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 'var(--secondary-navigation-control-gap, 4px)',
    justifyContent: 'center',
    left: '50%',
    lineHeight: 'var(--secondary-navigation-line-height, 16px)',
    opacity: contentPosition === 'active' ? 1 : 0,
    position: 'absolute',
    top: '50%',
    transform: `translate(-50%, -50%) translateY(${contentTranslateY})`,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: 260,
      easing: theme.transitions.easing.easeInOut,
    }),
    whiteSpace: 'nowrap',
  };
});

export const SecondaryNavigationActionStatusIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: 'currentColor',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--secondary-navigation-action-icon-size, 16px)',
  height: 'var(--secondary-navigation-action-icon-size, 16px)',
  justifyContent: 'center',
  opacity: 0.95,
  transformOrigin: 'center',
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--secondary-navigation-action-icon-size, 16px)',
}));

export const SecondaryNavigationActionSavedCheck = styled('svg')({
  display: 'block',
  height: 'var(--secondary-navigation-action-icon-size, 16px)',
  overflow: 'visible',
  width: 'var(--secondary-navigation-action-icon-size, 16px)',
});

export const SecondaryNavigationActionSavedCheckPath = styled('path')({
  animation: `${secondaryNavigationSavedCheckDraw} 420ms ease-out 80ms both`,
  fill: 'none',
  stroke: 'currentColor',
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: 2,
});

export const SecondaryNavigationIconButtonRoot = styled(MuiIconButton, {
  shouldForwardProp: shouldForwardIconButtonProp,
})<IconButtonStyleProps>(({ iconButtonVariant, isActive, theme }) => {
  const { iconButton } = theme.palette.navigation.secondary;
  const isContained = iconButtonVariant === 'contained';
  const defaultBackground = isActive ? iconButton.activeBackground : 'transparent';
  const defaultColor = isActive ? iconButton.activeText : iconButton.defaultText;
  const defaultHoverBackground = isActive ? iconButton.activeBackground : iconButton.hoverBackground;
  const iconButtonSize = isContained
    ? 'var(--secondary-navigation-contained-icon-button-size, 20px)'
    : 'var(--secondary-navigation-plain-icon-button-size, 24px)';

  return {
    backgroundColor: isContained ? iconButton.containedBackground : defaultBackground,
    borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
    color: isContained ? iconButton.containedText : defaultColor,
    height: iconButtonSize,
    padding: isContained ? 'var(--secondary-navigation-control-padding-y, 2px)' : 0,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: 160,
      easing: theme.transitions.easing.easeInOut,
    }),
    width: iconButtonSize,
    '&:hover': {
      backgroundColor: isContained ? iconButton.containedHoverBackground : defaultHoverBackground,
      color: isContained ? iconButton.containedText : iconButton.hoverText,
    },
    '&.Mui-disabled': {
      color: iconButton.disabledText,
      opacity: 1,
    },
    '& svg': {
      fontSize: isContained
        ? 'var(--secondary-navigation-action-icon-size, 16px)'
        : 'var(--secondary-navigation-plain-icon-button-size, 24px)',
    },
  };
});

export const SecondaryNavigationIconTooltipAnchor = styled('span')({
  display: 'inline-flex',
  flex: '0 0 auto',
});

export const SecondaryNavigationSelectorRoot = styled(MuiButton, {
  shouldForwardProp: shouldForwardSelectorProp,
})<SelectorStyleProps>(({ selectorVariant, theme }) => ({
  '--secondary-navigation-selector-label-color':
    selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterText
      : theme.palette.navigation.secondary.selector.label,
  '--secondary-navigation-selector-value-color':
    selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterText
      : theme.palette.navigation.secondary.selector.value,
  '--secondary-navigation-selector-icon-color':
    selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterText
      : theme.palette.navigation.secondary.selector.icon,
  alignItems: 'center',
  backgroundColor:
    selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterBackground
      : theme.palette.navigation.secondary.selector.background,
  borderColor: selectorVariant === 'filter' ? 'transparent' : theme.palette.navigation.secondary.selector.border,
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  borderStyle: 'solid',
  borderWidth: 'var(--secondary-navigation-border-width, 1px)',
  boxShadow: 'none',
  color: 'var(--secondary-navigation-selector-value-color)',
  display: 'inline-flex',
  fontSize: 'var(--secondary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  height: 'var(--secondary-navigation-control-height, 24px)',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-line-height, 16px)',
  maxWidth: selectorVariant === 'filter' ? 'var(--secondary-navigation-filter-selector-max-width, 172px)' : '100%',
  minHeight: 'unset',
  minWidth: 0,
  padding: 'var(--secondary-navigation-control-padding-y, 4px) var(--secondary-navigation-control-padding-x, 4px)',
  textTransform: 'none',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:hover': {
    '--secondary-navigation-selector-label-color':
      selectorVariant === 'filter'
        ? theme.palette.navigation.secondary.selector.filterText
        : theme.palette.navigation.secondary.selector.label,
    '--secondary-navigation-selector-value-color':
      selectorVariant === 'filter'
        ? theme.palette.navigation.secondary.selector.filterText
        : theme.palette.navigation.secondary.selector.hoverText,
    '--secondary-navigation-selector-icon-color':
      selectorVariant === 'filter'
        ? theme.palette.navigation.secondary.selector.filterText
        : theme.palette.navigation.secondary.selector.icon,
    backgroundColor:
      selectorVariant === 'filter'
        ? theme.palette.navigation.secondary.selector.filterHoverBackground
        : theme.palette.navigation.secondary.selector.hoverBackground,
    borderColor: selectorVariant === 'filter' ? 'transparent' : theme.palette.navigation.secondary.selector.hoverBorder,
    color: theme.palette.navigation.secondary.selector.hoverText,
  },
  '&.Mui-disabled': {
    '--secondary-navigation-selector-label-color': alpha(theme.palette.navigation.secondary.selector.label, 0.72),
    '--secondary-navigation-selector-value-color': alpha(theme.palette.navigation.secondary.selector.value, 0.72),
    '--secondary-navigation-selector-icon-color': alpha(theme.palette.navigation.secondary.selector.icon, 0.72),
    backgroundColor: theme.palette.navigation.secondary.selector.disabledBackground,
    borderColor: alpha(theme.palette.navigation.secondary.selector.border, 0.68),
    color: alpha(theme.palette.navigation.secondary.selector.value, 0.72),
    cursor: 'not-allowed',
    opacity: 1,
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    color: 'var(--secondary-navigation-selector-icon-color)',
    marginLeft: 0,
    marginRight: 0,
    '& svg': {
      fontSize: 'var(--secondary-navigation-action-icon-size, 16px)',
    },
  },
  '&:focus-visible': {
    outline: `var(--secondary-navigation-focus-outline-width, 2px) solid ${alpha(theme.palette.navigation.secondary.selector.focusOutline, 0.9)}`,
    outlineOffset: 'calc(var(--secondary-navigation-focus-outline-width, 2px) * -1)',
  },
}));

const secondaryNavigationSelectorTextStyles = {
  display: 'inline-block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const SecondaryNavigationSelectorLabel = styled('span')({
  ...secondaryNavigationSelectorTextStyles,
  color: 'var(--secondary-navigation-selector-label-color)',
});

export const SecondaryNavigationCollapsibleGroupRoot = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  gap: getThemeSpacingCssPx(theme, 's'),
}));

// Expand in place to reveal the values
export const SecondaryNavigationCollapsibleGroupFlyout = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.bar.background,
  display: 'flex',
  gap: getThemeSpacingCssPx(theme, 's'),
  height: getCssPx(32),
  paddingRight: getThemeSpacingCssPx(theme, 's'),
  whiteSpace: 'nowrap',
}));

export const SecondaryNavigationSelectorValue = styled('span', {
  shouldForwardProp: shouldForwardValueToneProp,
})<NavigationValueToneStyleProps>(({ theme, valueTone }) => ({
  ...secondaryNavigationSelectorTextStyles,
  color:
    valueTone === 'default'
      ? 'var(--secondary-navigation-selector-value-color)'
      : theme.palette.navigation.secondary.infoLabel.value[valueTone],
  transition: theme.transitions.create('color', {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
}));
